import { QueryClient } from "@tanstack/react-query";
import { Stack, styled, Typography } from "@mui/material";
import { LoaderFunctionArgs, redirect } from "react-router-dom";

import {
  convertQueryParamsToState,
  cognitoSignInUrlQuery,
} from "@/features/shared/features/authentication";

const ALLOWED_QUERY_PARAMS = ["state"];
const CALLBACK_URL = "/auth/callback";

const getQueryParams = (url: string) => {
  const params = new URL(url).searchParams;
  const queryParams: Record<string, string> = {};

  for (const key of params.keys()) {
    if (ALLOWED_QUERY_PARAMS.includes(key)) {
      queryParams[key] = params.get(key) || "";
    }
  }

  return queryParams;
};

export const loginLoader =
  (queryClient: QueryClient) =>
  async ({ request }: LoaderFunctionArgs) => {
    const params = getQueryParams(request.url);
    const state = convertQueryParamsToState(params);
    const callbackUrl = new URL(document.location.origin + CALLBACK_URL);

    try {
      const signInUrlQuery = await queryClient.fetchQuery(
        cognitoSignInUrlQuery(String(callbackUrl), state),
      );

      return redirect(signInUrlQuery.data.url);
    } catch {
      // The Cognito sign-in URL could not be fetched (e.g. the backend is
      // unreachable). Instead of redirecting, resolve the route to an element
      // that explains signing in is currently unavailable.
      return <LoginUnavailable />;
    }
  };

const LoginUnavailable = () => (
  <Stack p={2} alignItems="center">
    <Stack spacing={2} sx={{ maxWidth: "552px", marginTop: "50px" }}>
      <StyledTypography>Inloggen is momenteel niet mogelijk</StyledTypography>
      <StyledBodyTypography variant="body1">
        Er kan op dit moment geen verbinding met het systeem worden gemaakt, waardoor de
        website niet bruikbaar is. Probeer het later opnieuw. Blijft het probleem zich
        voordoen, neem dan contact op met de administrator.
      </StyledBodyTypography>
    </Stack>
  </Stack>
);

const StyledTypography = styled("h4")(`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
`);

const StyledBodyTypography = styled(Typography)(`
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: 0.5px;
  margin-top: 0px!important;

  a {
    color: var(--blue);
    [data-theme="dark"] & {
      color: var(--blue-background);
    }
  }
`);
